import { useState } from "react";
import {
  Autocomplete,
  Button,
  Input,
  ListBox,
  ListBoxItem,
  SearchField,
  useFilter,
  type ListBoxItemProps,
} from "react-aria-components";

import { cn } from "@trip/utils";

import type { HomePageData } from "~/app/[locale]/(default)/_lib/get-home-page";
import { CheckmarkIcon } from "~/components/icons/checkmark-icon";
import { CloseIcon } from "~/components/icons/close-icon";
import { SearchIcon } from "~/components/icons/search-icon";
import { HR } from "~/components/typography";

export function MultiLocationPicker({
  locations,
  value,
  onChange,
}: {
  locations: HomePageData["allLocations"];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [seeAll, setSeeAll] = useState(false);
  const { contains } = useFilter({ sensitivity: "base" });

  const heroLocations = locations
    .filter((location) => location.isHero)
    .map((location) => ({
      ...location,
      id: location.slug,
    }));
  const allLocations = locations.map((location) => ({
    ...location,
    id: location.slug,
  }));
  const extraneouslySelectedOutsideHero = allLocations.filter(
    (location) =>
      value.includes(location.slug) &&
      !heroLocations.map((location) => location.slug).includes(location.slug),
  );
  const seeMoreLocations = allLocations.filter(
    (location) =>
      !heroLocations.map((location) => location.slug).includes(location.slug) &&
      !extraneouslySelectedOutsideHero
        .map((location) => location.slug)
        .includes(location.slug),
  );

  return (
    <>
      <Autocomplete
        disableAutoFocusFirst
        filter={contains}
        inputValue={query}
        onInputChange={(value) => {
          setQuery(value);
        }}
      >
        <SearchField
          data-slot="control"
          aria-label="Search"
          className="focus-within:outline-prune-500 shadow-field hover:shadow-field-hover group flex h-10 w-full items-center rounded-lg bg-white focus-within:outline-2"
        >
          <SearchIcon aria-hidden className="ml-3 h-5 w-5" />
          <Input
            placeholder="Search locations"
            type="text"
            className="placeholder:text-muted/80 min-w-0 flex-1 border-none bg-white px-2 py-1 text-sm font-normal outline-0 [&::-webkit-search-cancel-button]:hidden"
          />
          <Button className="pressed:bg-black/10 mr-1 flex w-6 items-center justify-center rounded-full border-0 bg-transparent p-1 text-center text-sm transition hover:bg-black/[5%] group-empty:invisible">
            <CloseIcon aria-hidden className="h-4 w-4" />
          </Button>
        </SearchField>

        <ListBox
          items={allLocations}
          selectedKeys={value}
          onSelectionChange={(selected) => {
            if (selected instanceof Set) {
              onChange([...selected].map((key) => key.toString()));
            }
          }}
          selectionMode="multiple"
          className={cn(
            "outline-hidden mt-1.5 max-h-[40vh] flex-1 scroll-pb-1 overflow-auto p-0.5",
            query.trim() === "" ? "hidden" : "",
          )}
        >
          {(item) => <SelectItem>{item.name}</SelectItem>}
        </ListBox>
      </Autocomplete>
      <div className="px-0.5">
        <ListBox
          items={heroLocations}
          selectedKeys={value}
          onSelectionChange={(selected) => {
            if (selected instanceof Set) {
              onChange([...selected].map((key) => key.toString()));
            }
          }}
          selectionMode="multiple"
          className={cn("mt-3", query.trim() === "" ? "" : "hidden")}
        >
          {(item) => <SelectItem key={item.slug}>{item.name}</SelectItem>}
        </ListBox>
        {extraneouslySelectedOutsideHero.length > 0 ? (
          <>
            <HR className="my-4" />
            <ListBox
              items={extraneouslySelectedOutsideHero}
              selectedKeys={value}
              onSelectionChange={(selected) => {
                if (selected instanceof Set) {
                  onChange([...selected].map((key) => key.toString()));
                }
              }}
              selectionMode="multiple"
            >
              {(item) => <SelectItem key={item.slug}>{item.name}</SelectItem>}
            </ListBox>
          </>
        ) : null}
        {seeAll ? (
          <ListBox
            items={seeMoreLocations}
            selectedKeys={value}
            onSelectionChange={(selected) => {
              if (selected instanceof Set) {
                onChange([...selected].map((key) => key.toString()));
              }
            }}
            selectionMode="multiple"
          >
            {(item) => <SelectItem key={item.slug}>{item.name}</SelectItem>}
          </ListBox>
        ) : null}
      </div>
      <button
        className="text-prune-500 mt-2 text-sm font-medium hover:underline"
        type="button"
        onClick={() => {
          setSeeAll((prev) => !prev);
        }}
      >
        {seeAll ? "Show less" : "Show more"}
      </button>
    </>
  );
}

function SelectItem(props: ListBoxItemProps & { children: string }) {
  return (
    <ListBoxItem
      {...props}
      textValue={props.children}
      className="outline-hidden data-focused:bg-neutral-200 data-focused:outline-2 data-focused:outline-neutral-200 group mb-1.5 flex cursor-default select-none items-center gap-2 rounded-sm py-px"
    >
      {({ isSelected }) => (
        <>
          <span className="group-data-selected:text-white group-data-selected:bg-neutral-900 group-data-selected:ring-neutral-900 group-data-focused:ring-prune-500 flex h-5 w-5 items-center rounded-sm ring-1 ring-neutral-900/30 hover:ring-neutral-500">
            {isSelected ? <CheckmarkIcon /> : null}
          </span>
          <span className="flex flex-1 items-center gap-2 truncate text-sm font-normal">
            {props.children}
          </span>
        </>
      )}
    </ListBoxItem>
  );
}
